import { forwardRef } from 'react';
import {
  Button as SharedButton,
  type ButtonProps as SharedButtonProps,
} from 'pi-kiosk-shared/ui';

export type ButtonProps = Omit<SharedButtonProps, 'surface'>;

function classForcesFullWidth(className: string | undefined): boolean {
  if (className === undefined || className.length === 0) {
    return false;
  }
  return /(^|\s)w-full(\s|$)/.test(className);
}

/** C4 — visual height from VCL; md floor 44; lg/xl floor 48 (via --vcl-control-height-lg). */
const C4_SIZE: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'min-h-[max(44px,var(--vcl-control-height-md))] min-w-[44px]',
  md: 'h-[var(--vcl-control-height-md)] min-h-[max(44px,var(--vcl-control-height-md))] px-[var(--vcl-control-pad-x)]',
  lg: 'h-[var(--vcl-control-height-lg)] min-h-[max(48px,var(--vcl-control-height-lg))] px-[var(--vcl-control-pad-x)]',
  xl: 'h-[var(--vcl-control-height-lg)] min-h-[max(48px,var(--vcl-control-height-lg))] px-[var(--vcl-control-pad-x)]',
};

/**
 * AC-10 (A): state-based press — not opacity-only, not delay-only.
 * AC-10 (B): must-not-slow — ≤150ms (duration-75); no animation await.
 * RM: drop transform; keep brightness so meaning survives (EC-06).
 */
const AC10_PRESS_CLASS = [
  'active:brightness-95',
  'active:scale-[0.98]',
  'transition-[filter,transform,background-color,border-color,color] duration-75',
  'motion-reduce:transition-none motion-reduce:active:scale-100',
].join(' ');

/**
 * Customer surface Button — inherits shared recipe (`max-w-sm` CTA cap).
 * Callers that pass `className="w-full"` (without `block`) still get `mx-auto`
 * so the capped control stays mid-centered in wide panels.
 * C4 + AC-10 overrides are customer-adapter-only (do not edit shared recipe).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { className, block, size = 'md', ...rest } = props;
  const centerFullWidth = block === true || classForcesFullWidth(className);
  const mergedClassName = [
    C4_SIZE[size],
    AC10_PRESS_CLASS,
    centerFullWidth && block !== true ? 'mx-auto' : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <SharedButton
      ref={ref}
      surface="customer"
      block={block}
      size={size}
      className={mergedClassName}
      {...rest}
    />
  );
});
Button.displayName = 'Button';
